import { useRef, useState, useCallback } from 'react';
import { useField } from 'formik';
import { FieldWrapper, type FieldWrapperConfig } from '@/form/FieldWrapper';
import {
  X,
  FileText,
  Plus,
  Upload,
  Download,
  ExternalLink,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { TranslationNS } from '@gokrew/models/constants';
import { isAcceptedFileType } from './isAcceptedFileType';
import { formatFileSize, getDisplayFileName } from '@/utility/utility';
import styles from './DropzoneField.module.css';

type SingleProps = {
  multiple?: false;
  max?: never;
};

type MultipleProps = {
  multiple: true;
  max?: number;
};

/**
 * What one slot in a dropzone holds: a file the user just picked, or the URL
 * of one already stored on the record.
 */
export type DropzoneValue = File | string;

type DropzoneFieldProps = FieldWrapperConfig & {
  name: string;
  accept?: string;
  acceptLabel?: string;
  disabled?: boolean;
  maxSizeMB?: number;
} & (SingleProps | MultipleProps);

const DropzoneField = ({
  name,
  label,
  infoText,
  disabled = false,
  fieldClassName,
  accept = '.pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.webp',
  acceptLabel,
  maxSizeMB = 5,
  multiple,
  max = 5,
}: DropzoneFieldProps) => {
  const { t } = useTranslation(TranslationNS.LibsCommon);
  const { t: tFilters } = useTranslation('filters');
  const [field, meta, helpers] = useField(name);
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const hasError = meta.touched && !!meta.error;

  const finalAcceptLabel = acceptLabel || tFilters('dropzone.accept_label');
  // ── Single mode ──────────────────────────────────────────────────────────
  const file: DropzoneValue | null = !multiple ? (field.value ?? null) : null;

  // ── Multiple mode ────────────────────────────────────────────────────────
  // A string entry is a file already stored on the record, pre-filled by the
  // form; only a `File` is something the user just picked.
  const files: DropzoneValue[] = multiple ? (field.value ?? []) : [];
  const atMax = multiple && files.length >= max;

  // ── Shared helpers ────────────────────────────────────────────────────────
  const validateType = (f: File): boolean => {
    if (!isAcceptedFileType(f, accept)) {
      helpers.setError(t('dropzone.file_type_rejected', { name: f.name }));
      return false;
    }
    return true;
  };

  const validateSize = (f: File): boolean => {
    if (maxSizeMB && f.size > maxSizeMB * 1024 * 1024) {
      helpers.setError(
        t('dropzone.file_size_exceeded', { name: f.name, size: maxSizeMB })
      );
      return false;
    }
    return true;
  };

  // ── Single handlers ───────────────────────────────────────────────────────
  const handleSingleFile = useCallback(
    (f: File | null) => {
      if (!f) {
        helpers.setValue(null);
        helpers.setError(undefined);
        helpers.setTouched(false);
        return;
      }
      helpers.setTouched(true, false);
      if (!validateType(f)) return;
      if (!validateSize(f)) return;
      helpers.setValue(f, true);
      helpers.setError(undefined);
    },
    [helpers, accept, maxSizeMB]
  );

  const handleSingleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleSingleFile(e.target.files?.[0] ?? null);
    e.target.value = '';
  };

  const handleSingleRemove = (e: React.MouseEvent) => {
    e.stopPropagation();
    helpers.setValue(null, true);
    helpers.setTouched(true, false);
  };

  // ── Multiple handlers ─────────────────────────────────────────────────────
  const handleMultipleFiles = useCallback(
    (incoming: FileList | null) => {
      if (!incoming) return;
      helpers.setTouched(true, false);

      const current: DropzoneValue[] = field.value ?? [];
      const slots = max - current.length;
      if (slots <= 0) return;

      const toAdd: File[] = [];
      let hadRejection = false;
      for (let i = 0; i < Math.min(incoming.length, slots); i++) {
        const f = incoming[i];
        if (!validateType(f)) {
          hadRejection = true;
          continue;
        }
        if (!validateSize(f)) {
          hadRejection = true;
          continue;
        }
        // deduplicate by name + size
        if (
          current.some(
            (c) =>
              typeof c !== 'string' && c.name === f.name && c.size === f.size
          )
        ) {
          toast.error(t('error.file_exists'));
          continue;
        }
        toAdd.push(f);
      }

      // A rejection's error (set by validateType/validateSize above) should
      // stay visible even though the valid files in the same batch were
      // still added — only clear it once every file in the batch passed.
      // shouldValidate must stay false on a rejection: Formik's own
      // (schema-based) validation has no rule for this field, so letting it
      // run here would resolve moments later and silently wipe the
      // manually-set rejection error out from under the user.
      helpers.setValue([...current, ...toAdd], !hadRejection);
      if (!hadRejection) helpers.setError(undefined);
    },
    [helpers, field.value, max, accept, maxSizeMB]
  );

  const handleMultipleInputChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    handleMultipleFiles(e.target.files);
    e.target.value = '';
  };

  const handleMultipleRemove = (e: React.MouseEvent, index: number) => {
    e.stopPropagation();
    const next = files.filter((_, i) => i !== index);
    helpers.setValue(next, true);
    helpers.setTouched(true, false);
  };

  // ── Shared drag handlers ──────────────────────────────────────────────────
  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    if (disabled) return;
    if (multiple) handleMultipleFiles(e.dataTransfer.files);
    else handleSingleFile(e.dataTransfer.files?.[0] ?? null);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (!disabled) setIsDragging(true);
  };

  const handleDragLeave = () => setIsDragging(false);

  const openFilePicker = () => {
    if (!disabled) inputRef.current?.click();
  };

  // ── Class helpers ─────────────────────────────────────────────────────────
  const dropzoneBoxClass = [
    styles.dropzoneBox,
    isDragging ? styles.dropzoneBoxDragging : '',
    disabled ? styles.dropzoneBoxDisabled : '',
    hasError ? styles.dropzoneBoxError : '',
  ]
    .filter(Boolean)
    .join(' ');

  const iconWrapClass = [
    styles.dropzoneIconWrap,
    isDragging ? styles.dropzoneIconWrapDragging : '',
    hasError ? styles.dropzoneIconWrapError : '',
  ]
    .filter(Boolean)
    .join(' ');

  const iconClass = [
    styles.dropzoneIcon,
    isDragging ? styles.dropzoneIconDragging : '',
    hasError ? styles.dropzoneErrorIcon : '',
  ]
    .filter(Boolean)
    .join(' ');

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <FieldWrapper
      name={name}
      label={label}
      infoText={infoText}
      disabled={disabled}
      errorMeta={meta}
      fieldClassName={fieldClassName}
    >
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        multiple={multiple}
        disabled={disabled}
        className="hidden"
        onChange={
          multiple ? handleMultipleInputChange : handleSingleInputChange
        }
      />

      {/* ── MULTIPLE MODE ─────────────────────────────────────────── */}
      {multiple ? (
        <div className={styles.dropzoneMultipleWrapper}>
          {/* File stack — rendered above the dropzone */}
          {files.length > 0 && (
            <ul className={styles.dropzoneFileList}>
              {files.map((f, i) => {
                const stored = typeof f === 'string';

                return (
                  <li
                    key={stored ? `${f}-${i}` : `${f.name}-${f.size}-${i}`}
                    className={styles.dropzoneFileItem}
                  >
                    <div className={styles.dropzoneFileItemLeftside}>
                      <FileText className={styles.dropzoneFileIcon} />
                      <div className={styles.dropzoneFilenameInfo}>
                        <span className={styles.dropzoneFileName}>
                          {stored ? getDisplayFileName(f) : f.name}
                        </span>
                        {/* A stored file has no size to report, so its slot
                            carries the link to open it instead. */}
                        {stored ? (
                          <a
                            href={f}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className={styles.dropzoneFileLink}
                          >
                            {t('file_upload.view')}
                            <ExternalLink size={12} aria-hidden="true" />
                          </a>
                        ) : (
                          <span className={styles.dropzoneFileSize}>
                            {formatFileSize(f.size)}
                          </span>
                        )}
                      </div>
                    </div>
                    {!disabled && (
                      <button
                        type="button"
                        onClick={(e) => handleMultipleRemove(e, i)}
                        className={styles.dropzoneFileRemove}
                      >
                        <X size={14} />
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>
          )}

          {/* Dropzone / Add-more row */}
          {!atMax &&
            (files.length > 0 ? (
              /* "Add more files" compact row */
              <div
                role="button"
                tabIndex={disabled ? -1 : 0}
                aria-disabled={disabled}
                onClick={openFilePicker}
                onKeyDown={(e) => {
                  if (!disabled && (e.key === 'Enter' || e.key === ' ')) {
                    e.preventDefault();
                    openFilePicker();
                  }
                }}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                className={styles.dropzoneAddMore}
              >
                <Plus size={16} />
                {t('dropzone.click_to_add_more')}
              </div>
            ) : (
              /* Full dropzone */
              <div
                role="button"
                tabIndex={disabled ? -1 : 0}
                aria-disabled={disabled}
                onClick={openFilePicker}
                onKeyDown={(e) => {
                  if (!disabled && (e.key === 'Enter' || e.key === ' ')) {
                    e.preventDefault();
                    openFilePicker();
                  }
                }}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                className={dropzoneBoxClass}
              >
                <div className={iconWrapClass}>
                  {isDragging ? (
                    <Download size={28} className={iconClass} />
                  ) : (
                    <Upload size={28} className={iconClass} />
                  )}
                </div>
                <div className={styles.dropzoneHeading}>
                  {hasError ? (
                    <>
                      <p className={styles.dropzoneErrorHeading}>
                        {t('dropzone.error_heading')}
                      </p>
                      <p className={styles.dropzoneErrorSub}>{meta.error}</p>
                    </>
                  ) : (
                    <>
                      <p className={styles.dropzoneHeading}>
                        {isDragging
                          ? t('dropzone.drop_files_here')
                          : t('dropzone.click_to_select_files')}
                      </p>
                      <p className={styles.dropzoneSub}>{finalAcceptLabel}</p>
                    </>
                  )}
                </div>
              </div>
            ))}
        </div>
      ) : (
        /* ── SINGLE MODE ──────────────────────────────────────────── */
        <>
          {file ? (
            <div className={styles.dropzoneFileItem}>
              <div className={styles.dropzoneFileItemLeftside}>
                <FileText size={20} className={styles.dropzoneFileIcon} />
                <div className={styles.dropzoneFilenameInfo}>
                  <span className={styles.dropzoneFileName}>
                    {typeof file === 'string'
                      ? getDisplayFileName(file)
                      : file.name}
                  </span>
                  {/* A stored file has no size to report, so its slot carries
                      the link to open it instead. */}
                  {typeof file === 'string' ? (
                    <a
                      href={file}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={styles.dropzoneFileLink}
                    >
                      {t('file_upload.view')}
                      <ExternalLink size={12} aria-hidden="true" />
                    </a>
                  ) : (
                    <span className={styles.dropzoneFileSize}>
                      {formatFileSize(file.size)}
                    </span>
                  )}
                </div>
              </div>
              {!disabled && (
                <button
                  type="button"
                  onClick={handleSingleRemove}
                  className={styles.dropzoneFileRemove}
                >
                  <X size={14} />
                </button>
              )}
            </div>
          ) : (
            <div
              role="button"
              tabIndex={disabled ? -1 : 0}
              aria-disabled={disabled}
              onClick={openFilePicker}
              onKeyDown={(e) => {
                if (!disabled && (e.key === 'Enter' || e.key === ' ')) {
                  e.preventDefault();
                  openFilePicker();
                }
              }}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              className={dropzoneBoxClass}
            >
              <div className={iconWrapClass}>
                {isDragging ? (
                  <Download size={28} className={iconClass} />
                ) : (
                  <Upload size={28} className={iconClass} />
                )}
              </div>

              {/* A div, like the multiple box: a <p> may not hold the two <p>s. */}
              <div className={styles.dropzoneHeading}>
                {hasError ? (
                  <>
                    <p className={styles.dropzoneErrorHeading}>
                      {t('dropzone.error_heading')}
                    </p>
                    <p className={styles.dropzoneErrorSub}>{meta.error}</p>
                  </>
                ) : (
                  <>
                    <p className={styles.dropzoneHeading}>
                      {isDragging
                        ? t('dropzone.drop_files_here')
                        : t('dropzone.click_to_select_files')}
                    </p>
                    <p className={styles.dropzoneSub}>{finalAcceptLabel}</p>
                  </>
                )}
              </div>
            </div>
          )}
        </>
      )}
    </FieldWrapper>
  );
};

export { DropzoneField };
